import { z } from "zod";

/**
 * Zod schemas for `GET deep-research/responses` (PREC-3700 onboarding tour).
 * The response JSON mirrors the shape already consumed by the Profile Tracker
 * panel (`People["deepResearchResponse"]`): a record of sections keyed by
 * snake_case header, each with an order, markdown content and optional
 * subsections.
 */

/**
 * A citation the research agent attached to a fact (PREC-3680). Only present on
 * results produced after that change — every consumer must render without it.
 */
export const DeepResearchSourceSchema = z.object({
	url: z.string(),
	title: z.string(),
	domain: z.string().optional().default(""),
	kind: z.enum(["web", "linkedin", "news", "doc"]).optional().default("web"),
});

/** One entry of the Talking Points section's structured `points` array. */
export const DeepResearchPointSchema = z.object({
	title: z.string().optional().default(""),
	text: z.string(),
	sources: z.array(DeepResearchSourceSchema).optional(),
});

export const DeepResearchResponseSectionSchema = z.object({
	order: z.coerce.number(),
	content: z.string().optional().default(""),
	points: z.array(DeepResearchPointSchema).optional(),
	sources: z.array(DeepResearchSourceSchema).optional(),
	subsections: z
		.array(
			z.object({
				order: z.coerce.number(),
				header: z.string(),
				content: z.string(),
				sources: z.array(DeepResearchSourceSchema).optional(),
			})
		)
		.optional(),
});

export const DeepResearchResponseInfoSchema = z.object({
	id: z.number(),
	prospectId: z.number(),
	deepResearchPromptId: z.number().optional(),
	response: z.record(z.string(), DeepResearchResponseSectionSchema).nullable().optional(),
	fitAssessment: z.string().nullable().optional(),
	fitReason: z.string().nullable().optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
	prospect: z
		.object({
			id: z.number(),
			name: z.string().nullable().optional(),
			title: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
});

export const DeepResearchResponsesListSchema = z.array(DeepResearchResponseInfoSchema);

/**
 * One prospect's latest deep-research run that is not finished, or failed (PREC-4052). "queued" is
 * not yet handed to a worker, "running" is; a failure says whether a retry can succeed.
 */
export const DeepResearchRunSchema = z.object({
	prospectId: z.number(),
	state: z.enum(["queued", "running", "failed"]),
	createdAt: z.string(),
	publishedAt: z.string().nullable(),
	failedAt: z.string().nullable(),
	failureReason: z.enum(["insufficient_credits", "failed"]).nullable(),
});

/**
 * `GET deep-research/pending` — the asked-about prospects with a run queued or running
 * (PREC-4218), and the latest queued, running or failed run of each (PREC-4052). `runs` is
 * optional: a release before PREC-4052 answers without it.
 */
export const DeepResearchPendingSchema = z.object({
	prospectIds: z.array(z.number()),
	runs: z.array(DeepResearchRunSchema).optional(),
});

export type DeepResearchRun = z.infer<typeof DeepResearchRunSchema>;
export type DeepResearchPending = z.infer<typeof DeepResearchPendingSchema>;

export type DeepResearchSource = z.infer<typeof DeepResearchSourceSchema>;
export type DeepResearchPoint = z.infer<typeof DeepResearchPointSchema>;
export type DeepResearchResponseSection = z.infer<typeof DeepResearchResponseSectionSchema>;
export type DeepResearchResponseInfo = z.infer<typeof DeepResearchResponseInfoSchema>;

/** Research facts a user pinned for the copywriter (PREC-3680). */
export const PinnedFactSchema = z.object({
	id: z.number(),
	prospectId: z.number(),
	deepResearchResponseId: z.number().nullable().optional(),
	factKey: z.string(),
	factLabel: z.string().nullable().optional(),
	factText: z.string(),
	sourceUrls: z.array(z.string()).nullable().optional(),
	createdAt: z.string().optional(),
});

export const PinnedFactsListSchema = z.array(PinnedFactSchema);

export type PinnedFact = z.infer<typeof PinnedFactSchema>;

export interface CreatePinnedFactRequest {
	prospectId: number;
	deepResearchResponseId?: number;
	factKey: string;
	factLabel?: string;
	factText: string;
	sourceUrls?: string[];
}

export interface ListDeepResearchResponsesParams {
	prospectId?: number;
	limit?: number;
}

/** AI suggestions to refine a Deep Research prompt from a critique (PREC-3719 block 8). */
export const DeepResearchPromptSuggestionSchema = z.object({
	id: z.string(),
	header: z.string(),
	sectionIndex: z.coerce.number(),
	before: z.string(),
	after: z.string(),
	reason: z.string(),
});

export const PromptSuggestionsResponseSchema = z.object({
	suggestions: z.array(DeepResearchPromptSuggestionSchema),
});

export type DeepResearchPromptSuggestion = z.infer<typeof DeepResearchPromptSuggestionSchema>;
export type PromptSuggestionsResponse = z.infer<typeof PromptSuggestionsResponseSchema>;

export interface GeneratePromptSuggestionsRequest {
	promptId: number;
	reasons: string[];
	note?: string;
}
